import { Pagination, Select } from "@nexus/ui";

import type { ListPages } from "@/hooks/usePagedList";
import { PAGE_SIZE_OPTIONS, pageSummary } from "@/lib/pagedList";
import { cn } from "@/lib/utils";

/**
 * Sticky page navigation and persisted row count, shared by collection screens.
 */
export function ListPagination({
  label,
  pages,
  className = "mt-3",
}: {
  /** Accessible name, e.g. "Tenant pages". */
  label: string;
  pages: Pick<
    ListPages<unknown>,
    "page" | "pageCount" | "setPage" | "pageSize" | "setPageSize" | "offset" | "totalCount"
  > & {
    rows: readonly unknown[];
  };
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "sticky bottom-0 z-20 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border bg-surface px-3 py-2 text-xs text-secondary",
        className,
      )}
    >
      <span className="mr-auto tabular-nums" role="status">
        {pageSummary(pages.offset, pages.rows.length, pages.totalCount)}
      </span>
      <div className="flex shrink-0 items-center gap-2">
        <span>Rows per page</span>
        <Select
          label={`${label}: rows per page`}
          value={String(pages.pageSize)}
          options={PAGE_SIZE_OPTIONS.map((size) => ({ value: String(size), label: String(size) }))}
          onValueChange={(value) => pages.setPageSize(Number(value))}
          controlSize="sm"
          className="w-20"
        />
      </div>
      <Pagination
        label={label}
        page={pages.page}
        pageCount={pages.pageCount}
        onPageChange={pages.setPage}
      />
    </div>
  );
}
